import React, { useState, useMemo } from 'react';
import { Search, Filter } from 'lucide-react';
import EPaperCard from './EPaperCard';
import { EPaperListProps } from '../types/epaper';
import ViewToggle, { ViewMode } from './ViewToggle';
import Pagination from './Pagination';
import './EPaperList.css';

const BADGE_CONFIG: Record<string, { text: string; className: string }> = {
  handbook: { text: 'Handbook', className: 'badge-handbook' },
  review: { text: 'Review', className: 'badge-review' },
  outlook: { text: 'Outlook', className: 'badge-outlook' },
  special: { text: 'Special', className: 'badge-special' },
  annual: { text: 'Annual', className: 'badge-annual' },
  quarterly: { text: 'Quarterly', className: 'badge-quarterly' },
};

const EPaperList: React.FC<EPaperListProps> = ({
  epapers,
  specialEditions = [],
  onSelectEPaper,
  loading = false,
}) => {
  const [activeTab, setActiveTab] = useState<'weekly' | 'special'>('weekly');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState<string>('all');
  const [currentView, setCurrentView] = useState<ViewMode>('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  const years = useMemo(() => {
    const yearSet = new Set(epapers.map(ep => new Date(ep.date).getFullYear().toString()));
    return Array.from(yearSet).sort((a, b) => parseInt(b) - parseInt(a));
  }, [epapers]);

  const filteredEpapers = useMemo(() => {
    return epapers
      .filter(ep => {
        const matchesSearch = ep.title.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesYear =
          selectedYear === 'all' || new Date(ep.date).getFullYear().toString() === selectedYear;
        return matchesSearch && matchesYear;
      })
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [epapers, searchTerm, selectedYear]);

  const totalPages = Math.ceil(filteredEpapers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentEpapers = filteredEpapers.slice(startIndex, startIndex + itemsPerPage);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedYear]);

  if (loading) {
    return (
      <div className="epaper-list-loading">
        <div className="spinner"></div>
        <p>Loading E-Papers...</p>
      </div>
    );
  }

  return (
    <div className="epaper-list-container">
      <div className="epaper-list-header">
        <h1>E-Papers</h1>
        <p>Browse and read our E-Paper editions</p>
      </div>

      {specialEditions.length > 0 && (
        <div className="tab-bar">
          <button
            className={`tab-button ${activeTab === 'weekly' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('weekly')}
          >
            Daily
          </button>
          <button
            className={`tab-button ${activeTab === 'special' ? 'tab-active' : ''}`}
            onClick={() => setActiveTab('special')}
          >
            Special Editions ({specialEditions.length})
          </button>
        </div>
      )}

      {activeTab === 'weekly' ? (
        <>
          <div className="epaper-list-controls">
            <div className="controls-left">
              <div className="search-container">
                <Search size={20} />
                <input
                  type="text"
                  placeholder="Search E-Papers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>
              <div className="filter-container">
                <Filter size={20} />
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="year-filter"
                >
                  <option value="all">All Years</option>
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="controls-right">
              <ViewToggle
                currentView={currentView}
                onViewChange={setCurrentView}
              />
            </div>
          </div>

          <div className="epaper-list-stats">
            <span>{filteredEpapers.length} of {epapers.length} E-Papers</span>
          </div>

          {currentEpapers.length === 0 ? (
            <div className="epaper-list-empty">
              <p>No E-Papers found matching your criteria.</p>
            </div>
          ) : (
            <>
              <div className={`epaper-container ${currentView === 'list' ? 'epaper-list-view' : 'epaper-grid'}`}>
                {currentEpapers.map((epaper) => (
                  <EPaperCard
                    key={epaper.id}
                    epaper={epaper}
                    onClick={() => onSelectEPaper(epaper)}
                    viewMode={currentView}
                  />
                ))}
              </div>

              {totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                  itemsPerPage={itemsPerPage}
                  totalItems={filteredEpapers.length}
                />
              )}
            </>
          )}
        </>
      ) : (
        <div className="epaper-container epaper-grid">
          {specialEditions.map((edition) => {
            const badge = edition.category ? BADGE_CONFIG[edition.category] : null;
            return (
              <div key={edition.id} className="special-edition-card">
                <EPaperCard
                  epaper={edition}
                  onClick={() => onSelectEPaper(edition)}
                  viewMode="grid"
                />
                {badge && (
                  <div className={`special-edition-badge ${badge.className}`}>
                    {badge.text}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EPaperList;
